import { useEffect, useState } from 'react';
import { Tabs, Tab, StandardButton, CircularActivityIndicator } from '@cxc/react-fds-components';
import { FeatureText, GroupedBasicCard } from '@cxc/react-hydro-components';
import styles from './TabbedSection.module.scss';
import './TabbedSection.scss';
import { aemContent } from '../../state/globalState';
import { htmlReactParserWrapper } from '../../utils/htmlReactParserWrapper';
import environment from '../../environment';
import { constructDigitalData, fireAnalyticsEvent } from '../../analytics/analytics';
import { ESWITCH_PAGE_SCROLL_ONCLICKS, ESWITCH_PAGE_TAB_ONCLICKS, GET_DETAILED_ASSESSMENT_BUTTON_ONCLICKS, GLOBAL_INTERACTION, PAGE_NAME } from '../../analytics/analyticsConstants';
import { useAtom } from 'jotai';
import { useUniversalContext } from '@cxc/react-universal-components';
import { URLConstants, marketConfiguration, ViewTypeConstants, AnalyticsConstants, SymbolConstants } from '../../utils/constants';
import { createSuperScriptLinkHtml } from '../SuperScriptLink/SuperScriptLink';

const TabbedSection = (): React.JSX.Element => {
    const [aem] = useAtom(aemContent);
    const [tabs, setTabs] = useState<any | null>([]);
    const [header, setHeader] = useState<any | null>(null);
    const [body, setBody] = useState<any | null>(null);
    const [tabIndex, setTabIndex] = useState<number>(0);
    const [sliderTextAvailable, setSliderTextAvailable] = useState<boolean>(false);
    const buttonConfiguration = ['boxContainer', 'textButton'];
    const buttonTypes = [ViewTypeConstants.QUICK_ESTIMATE, ViewTypeConstants.DETAILED_ASSESSMENT];
    const TABBED_SECTION = 'TabbedSection';
    const { navigate, language, isLoggedIn, subscribeScopedValue } = useUniversalContext();
    const cookiePreferences = subscribeScopedValue(AnalyticsConstants.FPG_COOKIES, AnalyticsConstants.COOKIE_PREFERENCES);
    const getStartedDisbaled = !marketConfiguration[language].enableGetStarted;

    useEffect(() => {
        initializeAemData();
    }, []);

    useEffect(() => {
        setHeaderAndBody();
    }, [tabs, tabIndex]);

    const initializeAemData = () => {
        const aemTabsJson = aem?.['tab-container']?.['props']?.['tabs'] || [];
        const aemTabs: any = [];
        for (const tab of aemTabsJson) {
            const aemCardsReferenceJson = tab?.['items'] || [];
            const buttonPayload = tab?.['items'][1] || [];
            const cardsReference: any = [];
            for (let i = 0; i < aemCardsReferenceJson?.[0]?.cardsReference?.length || 0; i++) {
                cardsReference.push({
                    key: i + 1,
                    bodyText: htmlReactParserWrapper(aemCardsReferenceJson[0].cardsReference[i].text),
                    headingText: htmlReactParserWrapper(aemCardsReferenceJson[0].cardsReference[i].heading),
                    icon: aemCardsReferenceJson[0].cardsReference[i].icon,
                    iconAltText: aemCardsReferenceJson[0].cardsReference[i].iconAltText,
                });
            }
            if (tab?.sliderText) {
                setSliderTextAvailable(true);
            }
            aemTabs.push({
                tabHeader: htmlReactParserWrapper(tab?.heading),
                sliderText: tab?.sliderText || null,
                tabText: htmlReactParserWrapper(tab?.text || null),
                buttonTitle: buttonPayload?.['buttonText'],
                buttonAriaLabel: buttonPayload?.['ariaLabel'],
                cards: cardsReference,
                detailHeading: htmlReactParserWrapper(aemCardsReferenceJson?.[1]?.heading || null),
                detailBody: htmlReactParserWrapper(aemCardsReferenceJson?.[1]?.text || null),
            });
        }
        setTabs(aemTabs);
    };

    const setHeaderAndBody = () => {
        if (tabs.length > 0) {
            const extractedHeader = tabs?.[tabIndex]?.detailHeading || '';
            const extractedBody = tabs?.[tabIndex]?.detailBody || '';
            setHeader(extractedHeader);
            setBody(extractedBody);
        }
    };

    const handleTabClicked = (index: number): void => {
        const prevTabIndex = tabIndex;
        setTabIndex(index);
        let tabName = ViewTypeConstants.QUICK_ESTIMATE;
        if (index === 1) {
            tabName = ViewTypeConstants.DETAILED_ASSESSMENT;
        }
        if (prevTabIndex !== index) {
            const digitalData = constructDigitalData('tabClick', PAGE_NAME, GLOBAL_INTERACTION, '', '', ESWITCH_PAGE_TAB_ONCLICKS + tabName, isLoggedIn, cookiePreferences);
            fireAnalyticsEvent(digitalData.digitalData, digitalData.directCallName);
        }
    };

    const renderCards = (cards: any[]) =>
        cards.length > 0 &&
        cards.map((item) => (
            <GroupedBasicCard key={item?.key} bodyText={item?.bodyText} bodyTextAlignment="center" buttonAlignment="center" headingText={item?.headingText} headingTextAlignment="center" icon={environment.AEM_URL + item?.icon} iconAlignment="center" iconAltText={item?.iconAltText} />
        ));

    const navigateToDetailedAssement = () => {
        navigate(URLConstants.DETAILED_ASSESSMENT);
    };

    const handleTabSectionButtonClick = (title: string, location: string) => {
        if (title === 'detailed-assessment') {
            navigateToDetailedAssement();
            const digitalData = constructDigitalData(title, PAGE_NAME, GLOBAL_INTERACTION, '', '', GET_DETAILED_ASSESSMENT_BUTTON_ONCLICKS, isLoggedIn, cookiePreferences);
            fireAnalyticsEvent(digitalData.digitalData, digitalData.directCallName);
        } else {
            const digitalData = constructDigitalData(title, PAGE_NAME, GLOBAL_INTERACTION, '', '', ESWITCH_PAGE_SCROLL_ONCLICKS + title, isLoggedIn, cookiePreferences);
            fireAnalyticsEvent(digitalData.digitalData, digitalData.directCallName);
        }

        document.getElementById('quick-estimate-calculator')?.scrollIntoView({ behavior: 'smooth' });
    };

    const renderButton = (item, index) => {
        if (buttonConfiguration[index] === 'boxContainer') {
            return (
                <div className={styles.BoxContainer}>
                    <StandardButton mode="light" target="_self" title={item?.buttonTitle || ''} type="button" ariaLabel={item?.buttonAriaLabel} validationMessage="" onClick={() => handleTabSectionButtonClick(buttonTypes[index], TABBED_SECTION)} />
                </div>
            );
        } else if (buttonConfiguration[index] === 'textButton') {
            return (
                <div className={styles.TextButton}>
                    <StandardButton title={item?.buttonTitle || ''} type="button" ariaLabel={item?.buttonAriaLabel} onClick={() => handleTabSectionButtonClick(buttonTypes[index], TABBED_SECTION)} disabled={getStartedDisbaled} />
                </div>
            );
        }
    };

    return (
        <div className="fds-layout-grid">
            <div className={`${styles.TabsContainer} fds-layout-grid__cell--span-12`}>
                {Object.keys(tabs).length !== 0 ? (
                    <>
                        {sliderTextAvailable ? (
                            <Tabs data-testid="slider-tabs" onTabChange={(tabIndex: number) => handleTabClicked(tabIndex)}>
                                {tabs.map((item, index) => (
                                    <Tab data-testid="slider-tabs" key={index} label={item?.sliderText || ''}>
                                        <FeatureText bodyText={item?.tabText || ''} disclosureBody="" disclosureTitle="" headingText={item?.tabHeader || ''} headingTextHtmlTag="h3" type="headingLarge" />
                                        <div>
                                            <div className={`fds-flex ${styles.tabContainer}`}>{renderCards(item.cards)}</div>
                                            {renderButton(item, index)}
                                        </div>
                                    </Tab>
                                ))}
                            </Tabs>
                        ) : (
                            <>
                                {tabs.map((item, index) => (
                                    <div key={index}>
                                        <FeatureText className={styles.headerText} bodyText={item?.tabText || ''} disclosureBody="" disclosureTitle="" headingText={item?.tabHeader || ''} headingTextHtmlTag="h3" type="headingLarge" />
                                        <div>
                                            <div className={`fds-flex ${styles.tabContainer}`}>{renderCards(item.cards)}</div>
                                            {renderButton(item, index)}
                                        </div>
                                    </div>
                                ))}
                            </>
                        )}
                    </>
                ) : (
                    <>
                        <CircularActivityIndicator />
                    </>
                )}
            </div>
            {header && body && (
                <>
                    <div className="fds-layout-grid__cell--span-12 fds-align--center">
                        <FeatureText bodyText={body} disclosureBody="" disclosureTitle="" headingText={header} headingTextHtmlTag="h3" type="headingLarge" />
                    </div>
                </>
            )}
        </div>
    );
};

export default TabbedSection;
